import { useEffect, useState } from "react";
import { AccessDecision } from "./Components/AccessDecision/AccessDecision";
import { DestinationState } from "shared/core/state/destinationState";
import { EnterClientCode } from "./Components/EnterClientCode/EnterClientCode";
import ViewLoading from "shared/UI/Spinners/ViewLoading";
import { useConfirmClientCodeFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { Location, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PageTitle from "UIPalette/PageTitle/PageTitle";
import { UseAuthResult } from "core/auth/model";
import { ClientInfoState } from "shared/core/state/clientInfoState";
import { trackBtnClicks, trackEvents, trackFormSubmission } from "core/monitoring/Events";
import { EventNames } from "core/monitoring/types/enums";

export const isChangeOrganisation = (location: Location) => location.state === "change_org";

export const Login = ({ auth, redirectToHome }: { auth: UseAuthResult; redirectToHome?: boolean }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const orgCode = searchParams.get("orgcode");
  const { isAuthenticated, signinRedirect, isLoading: authLoading } = auth;
  const navigate = useNavigate();
  const location = useLocation();

  const [clientCode, setClientCode] = useState(orgCode ?? ClientInfoState.getCode());
  const clientCodeEntered = !!clientCode;

  const { data, isLoading, error } = useConfirmClientCodeFunctionRequest(
    {
      clientCode: clientCode,
    },
    { shouldFetch: clientCodeEntered, shouldRetryOnError: false, needsBearerToken: false }
  );

  const handleReset = () => {
    trackEvents(EventNames.RESET_ORGANISATION_CODE);
    ClientInfoState.clear();
    setClientCode(undefined);
  };

  const handleSubmitConfirm = (action: "sign-in" | "sign-up", event?: string) => {
    trackBtnClicks(event ?? action);
    signinRedirect({ extraQueryParams: { client_code: clientCode, auth_action: action } });
  };

  const handleClickClientCode = (clientCode: string) => {
    trackFormSubmission("Client code", { clientCode });
    setClientCode(clientCode);
  };

  useEffect(() => {
    // Check for user already being logged in (due to auto-renewal of expired refresh token in the background)
    if (clientCode && data) {
      if (isAuthenticated && !orgCode && !isChangeOrganisation(location)) {
        navigate(DestinationState.get());
      } else if (!!orgCode) {
        const isNewOrgCode = orgCode.toLowerCase() !== ClientInfoState.getCode()?.toLowerCase();
        if (!isNewOrgCode || ClientInfoState.getCode() === undefined) {
          navigate(DestinationState.get());
        }
      }
    }
  }, [clientCode, data, isAuthenticated, location, navigate, orgCode]);

  useEffect(() => {
    if (clientCode && data) {
      ClientInfoState.set(data);
    }
  }, [clientCode, data]);

  if (redirectToHome) {
    return <Navigate to="/home" />;
  }

  if (isLoading || authLoading) {
    return <ViewLoading label={t("auth.loading")} />;
  }

  if (data === undefined) {
    return (
      <>
        <PageTitle title={t("pageTitle.login.clientCode")} />
        <EnterClientCode
          handleClick={handleClickClientCode}
          error={error ? t<string>([`auth.error.confirmCode.${error.response?.status}`, "auth.error.confirmCode.other"]) : undefined}
          initialClientCode={clientCode}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={t("pageTitle.login.accessDecision")} />
      <AccessDecision
        clientName={data?.clientName ?? ""}
        clientLogo={data?.clientLogo}
        clientCode={clientCode!}
        handleReset={handleReset}
        handleRedirect={handleSubmitConfirm}
        isAuthenticated={isAuthenticated}
      />
    </>
  );
};
